import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  lineCount,
  price,
  sectionSubtitle,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Brush Burger, layout A: a grunge black board in three columns. The middle column starts
 * under a script brand and a huge distressed title; the side columns start under burger photos
 * in the top corners. Each section opens with a torn brush-stroke banner, each row has its name
 * and price over a dashed rule with the description underneath.
 *
 * The look (texture, banners, rows) is shared with brush-burger-cards and brush-burger-hero.
 */

/* ------------------------------------------------------------------ shared look */

const svg = (markup: string) => `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;

/** Crumpled black paper: lit fractal noise as soft highlights and creases, plus fine grit. */
const CRUMPLE = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' width='900' height='900'>" +
    "<filter id='l' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.006' numOctaves='6' seed='7' stitchTiles='stitch'/>" +
    "<feDiffuseLighting surfaceScale='9' lighting-color='white'><feDistantLight azimuth='225' elevation='40'/></feDiffuseLighting>" +
    "<feColorMatrix type='matrix' values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 .2 0 0 0 -.1'/></filter>" +
    "<filter id='s' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.006' numOctaves='6' seed='7' stitchTiles='stitch'/>" +
    "<feDiffuseLighting surfaceScale='9' lighting-color='white'><feDistantLight azimuth='225' elevation='40'/></feDiffuseLighting>" +
    "<feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 -.8 0 0 0 .55'/></filter>" +
    "<filter id='g' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/>" +
    "<feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .09 0'/></filter>" +
    "<rect width='900' height='900' filter='url(#s)'/><rect width='900' height='900' filter='url(#l)'/><rect width='900' height='900' filter='url(#g)'/></svg>",
);

/** Grey dry-brush sweeps across the corners, as in a painted chalk wall. */
const SWEEPS = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>" +
    "<filter id='b' x='-20%' y='-40%' width='140%' height='180%'>" +
    "<feTurbulence type='fractalNoise' baseFrequency='.003 .07' numOctaves='3' seed='2' result='t'/>" +
    "<feColorMatrix in='t' type='matrix' values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 4.5 0 0 0 -1.7' result='st'/>" +
    "<feComposite in='SourceGraphic' in2='st' operator='in' result='s'/>" +
    "<feTurbulence type='fractalNoise' baseFrequency='.09' numOctaves='2' seed='4' result='n'/>" +
    "<feDisplacementMap in='s' in2='n' scale='7' xChannelSelector='R' yChannelSelector='G'/></filter>" +
    "<g fill='rgba(255,255,255,.17)'>" +
    "<g transform='translate(1480 150) rotate(-62)' filter='url(#b)'><rect x='-330' y='-90' width='660' height='180' rx='60'/></g>" +
    "<g transform='translate(1560 560) rotate(-75)' filter='url(#b)'><rect x='-200' y='-40' width='400' height='80' rx='30'/></g>" +
    "<g transform='translate(40 260) rotate(-72)' filter='url(#b)'><rect x='-240' y='-55' width='480' height='110' rx='40'/></g>" +
    "<g transform='translate(760 880) rotate(-24)' filter='url(#b)'><rect x='-260' y='-50' width='520' height='100' rx='40'/></g>" +
    '</g></svg>',
);

/** A torn brush stroke, used as a mask over the accent colour. */
const BANNER = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 100' preserveAspectRatio='none'>" +
    "<filter id='r' x='-5%' y='-30%' width='110%' height='160%'>" +
    "<feTurbulence type='fractalNoise' baseFrequency='.03 .35' numOctaves='3' seed='11'/>" +
    "<feDisplacementMap in='SourceGraphic' scale='16' xChannelSelector='R' yChannelSelector='G'/></filter>" +
    "<g filter='url(#r)'><path d='M16 20 L388 13 L384 86 L10 91 Z'/>" +
    "<rect x='2' y='30' width='24' height='9'/><rect x='378' y='62' width='20' height='8'/><rect x='4' y='66' width='14' height='6'/></g></svg>",
);

/** Scratches and specks knocked out of display type. */
const DISTRESS = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'>" +
    "<filter id='d' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='2' seed='9' stitchTiles='stitch'/>" +
    "<feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 -10 0 0 0 7.2'/></filter>" +
    "<rect width='300' height='300' filter='url(#d)'/></svg>",
);

/** A circle with a torn edge, for photos. */
export const BB_TORN = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>" +
    "<filter id='t' x='-10%' y='-10%' width='120%' height='120%'><feTurbulence type='fractalNoise' baseFrequency='.08' numOctaves='3' seed='5'/>" +
    "<feDisplacementMap in='SourceGraphic' scale='9' xChannelSelector='R' yChannelSelector='G'/></filter>" +
    "<circle cx='100' cy='100' r='91' filter='url(#t)'/></svg>",
);

export const BB_FAMILY_ID = 'brush-burger';
export const BB_FAMILY_NAME = 'Brush Burger';

export const BB_THEME: MenuTheme = {
  backgroundColor: '#121212',
  surfaceColor: '#1f1f1f',
  textColor: '#f4f4f4',
  primaryColor: '#ffffff',
  accentColor: '#f9c623',
  fontId: 'street',
  backgroundMediaId: null,
};

export const BB_VARIANTS: MenuStyleVariant[] = [
  { id: 'yellow', name: 'Black & yellow', theme: {} },
  { id: 'red', name: 'Black & red', theme: { accentColor: '#ec3b2f' } },
  { id: 'orange', name: 'Charcoal & orange', theme: { backgroundColor: '#2a2a2d', surfaceColor: '#36363a', accentColor: '#ff8c1a' } },
  { id: 'chalk', name: 'Black & white chalk', theme: { accentColor: '#f1eee6', backgroundColor: '#161616' } },
];

/** Paint the shared background on a design's root. */
export function bbRootCss(S: string): string {
  return `
${S}{background-color:var(--mb-bg);background-image:${SWEEPS},${CRUMPLE},radial-gradient(ellipse at 50% 35%,rgba(255,255,255,.06),transparent 70%);
  background-size:cover,56em 56em,100% 100%;background-position:center,0 0,center;background-repeat:no-repeat,repeat,no-repeat}
${S}.mb-has-bg{background-image:none}`;
}

/** The torn brush banner: accent paint with dark heading text. */
export function bbBannerCss(S: string, cls: string): string {
  return `
${S} .${cls}{position:relative;background:var(--mb-accent);color:var(--mb-bg);
  -webkit-mask:${BANNER} center/100% 100% no-repeat;mask:${BANNER} center/100% 100% no-repeat}`;
}

/** Scuffs on display type. */
export function bbDistressCss(S: string, selector: string): string {
  return `
${S} ${selector}{-webkit-mask-image:${DISTRESS};mask-image:${DISTRESS};-webkit-mask-size:14em 14em;mask-size:14em 14em}`;
}

/** A torn round photo on a slightly larger accent ring. */
export function bbPhotoCss(S: string, cls: string): string {
  return `
${S} .${cls}{position:relative;aspect-ratio:1;filter:drop-shadow(0 .8em 1.2em rgba(0,0,0,.65))}
${S} .${cls}::before{content:'';position:absolute;inset:-4%;background:var(--mb-accent);opacity:.9;transform:rotate(40deg);
  -webkit-mask:${BB_TORN} center/100% 100% no-repeat;mask:${BB_TORN} center/100% 100% no-repeat}
${S} .${cls} img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  -webkit-mask:${BB_TORN} center/100% 100% no-repeat;mask:${BB_TORN} center/100% 100% no-repeat}`;
}

export function bbPhoto(ctx: RenderContext, mediaId: string | null | undefined, cls: string, extra = ''): string {
  const img = image(ctx, mediaId, `${cls}-img`);
  return img ? `<div class="${cls}${extra ? ` ${extra}` : ''}">${img}</div>` : '';
}

/* ------------------------------------------------------------------ rows */

function rowPrice(ctx: RenderContext, item: MenuItemContent, p: string): string {
  if (!item.variants.length) return sellingPrice(ctx, item, `${p}-price`);
  return item.variants
    .map((v) => `<span class="${p}-price"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join(`<span class="${p}-slash">/</span>`);
}

/** Name and price over a dashed rule, description underneath. */
export function bbRow(ctx: RenderContext, item: MenuItemContent, p: string): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="${p}-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="${p}-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<span class="${p}-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</span>`
      : '';
  const badges = badge(ctx, item, `${p}-badge`);
  return [
    `<div class="${p}-row">`,
    `<div class="${p}-top"><div class="${p}-names">${name}${second}</div>`,
    badges ? `<span class="${p}-badges">${badges}</span>` : '',
    `<div class="${p}-prices">${rowPrice(ctx, item, p)}</div></div>`,
    description(ctx, item, `${p}-desc`),
    '</div>',
  ].join('');
}

/** A section: brush banner, script note, rows. */
export function bbSection(ctx: RenderContext, b: MenuBlock, p: string): string {
  const head = hasTitle(b.section) ? `<div class="${p}-banner">${sectionTitle(ctx, b.section, `${p}-bt`)}</div>` : '';
  const note = b.continued ? '' : sectionSubtitle(ctx, b.section, `${p}-note`);
  return `<div class="${p}-sec">${head}${note}${b.items.map((i) => bbRow(ctx, i, p)).join('')}</div>`;
}

/** Height of a row in em, matching bbRowsCss. */
export function bbRowHeight(board: MenuBoardContent, item: MenuItemContent, perLine: number): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  // A second-language name shares the line with the first.
  const name = 2 * 1.08 + 0.45;
  const lines = board.settings.showDescriptions
    ? (en || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
      (ar ? lineCount(item.descriptionAr, perLine, 2) * ARABIC_LINE : 0)
    : 0;
  return name + (lines ? 0.35 + lines * 1.1 * 1.3 : 0) + 1.3;
}

/** Height of a section heading in em, matching bbRowsCss. */
export function bbHeadHeight(board: MenuBoardContent, section: MenuSection, continued: boolean): number {
  const both = board.settings.language === 'both' && section.titleAr?.trim() && section.title?.trim();
  const banner = hasTitle(section) ? 3.9 + (both ? 1.9 : 0) + 1.2 : 0;
  const note = !continued && (section.subtitle?.trim() || section.subtitleAr?.trim()) ? 2 : 0;
  return banner + note;
}

export function bbColumn(board: MenuBoardContent, capacity: number, perLine: number): MenuColumn {
  return {
    capacity,
    blockCost: (section, items, continued) =>
      bbHeadHeight(board, section, continued) + items.reduce((sum, i) => sum + bbRowHeight(board, i, perLine), 0) + 1.4,
  };
}

export function bbRowsCss(S: string, p: string): string {
  return `
${bbBannerCss(S, `${p}-banner`)}
${S} .${p}-sec{display:flex;flex-direction:column;margin-bottom:1.4em}
${S} .${p}-banner{align-self:center;min-width:72%;max-width:100%;padding:.8em 2.4em .75em;margin-bottom:1.2em;text-align:center}
${S} .${p}-bt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.1em;line-height:1.1;letter-spacing:.06em;text-transform:uppercase;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-bt.mb-2nd{font-size:1.35em;letter-spacing:0}
${S} .${p}-note{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:1.3em;text-align:center;margin:-.4em 0 .6em;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-row{margin-bottom:1.3em}
${S} .${p}-top{display:flex;align-items:flex-start;gap:.7em;padding-bottom:.3em;
  border-bottom:.12em dashed color-mix(in srgb,var(--mb-text) 55%,transparent)}
${S} .${p}-names{flex:1 1 auto;min-width:0;display:flex;align-items:baseline;gap:.6em}
${S} .${p}-name{flex:0 1 auto;min-width:0;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;line-height:1.08;letter-spacing:.05em;
  text-transform:uppercase;color:var(--mb-accent);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-name2{flex:1 4 auto;min-width:3em;text-align:right;font-family:var(--mb-font-heading);font-size:1.3em;color:var(--mb-accent);opacity:.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${p}-prices{flex:0 0 auto;display:flex;align-items:baseline;gap:.35em;white-space:nowrap}
${S} .${p}-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;line-height:1.08;letter-spacing:.04em;color:var(--mb-accent)}
${S} .${p}-price small{font-size:.55em;opacity:.8;letter-spacing:.08em}
${S} .${p}-price-old{font-size:1.1em;opacity:.55;color:var(--mb-text)}
${S} .${p}-slash{color:var(--mb-accent);opacity:.5;font-size:1.3em}
${S} .${p}-badges{display:flex;gap:.3em;flex-shrink:0;align-self:center}
${S} .${p}-badge{font-size:.75em;font-weight:800;padding:.15em .55em;border-radius:.2em;background:var(--mb-accent);color:var(--mb-bg);white-space:nowrap;transform:rotate(-3deg)}
${S} .${p}-desc{font-size:1.1em;line-height:1.3;color:var(--mb-text);opacity:.92;margin-top:.35em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .${p}-desc.mb-ar{line-height:1.3}
`;
}

/** Script brand, big distressed title, spaced tagline. */
export function bbHeader(ctx: RenderContext, p: string, maxChars: number, oneLine = false): string {
  const { board } = ctx;
  const title = board.settings.title ?? board.name;
  // Two-line titles fit their longest word; one-line titles fit the whole.
  const longest = oneLine ? title : (title ?? '').split(/\s+/).reduce((a, w) => (w.length > a.length ? w : a), '');
  const scale = fitScale(longest, maxChars, 0.45);
  return [
    `<div class="${p}-header">`,
    bilingual(ctx, board.brand.name, board.brand.nameAr, `${p}-brand`),
    `<div class="${p}-titles" style="font-size:${scale.toFixed(3)}em">`,
    bilingual(ctx, title, board.settings.titleAr, `${p}-title`),
    '</div>',
    bilingual(ctx, board.brand.tagline, board.brand.taglineAr, `${p}-tagline`),
    '</div>',
  ].join('');
}

export function bbHeaderCss(S: string, p: string, titleEm: number): string {
  return `
${S} .${p}-header{text-align:center;position:relative;z-index:1}
${S} .${p}-brand{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:2.8em;line-height:1.2;margin-bottom:.15em;transform:rotate(-3deg)}
${S} .${p}-brand.mb-2nd{font-size:1.8em}
${S} .${p}-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:${titleEm}em;line-height:.92;letter-spacing:.04em;
  text-transform:uppercase;color:var(--mb-primary);text-shadow:.03em .03em 0 rgba(0,0,0,.6);
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;padding-top:.04em}
${S} .${p}-title.mb-2nd{font-size:${(titleEm * 0.45).toFixed(2)}em;letter-spacing:0;line-height:1.3;-webkit-line-clamp:1}
${bbDistressCss(S, `.${p}-title:not(.mb-ar)`)}
${S} .${p}-tagline{font-size:1.15em;font-weight:600;letter-spacing:.24em;text-transform:uppercase;line-height:1.45;margin-top:.7em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .${p}-tagline.mb-2nd{letter-spacing:0;margin-top:.15em;-webkit-line-clamp:1}
`;
}

/** Height of bbHeader in em; title assumed to take two lines. */
export function bbHeaderHeight(board: MenuBoardContent, titleEm: number, titleLines: number): number {
  const both = board.settings.language === 'both';
  const brand = board.brand.name || board.brand.nameAr ? 2.8 * 1.35 + (both && board.brand.nameAr ? 1.8 * 1.3 * 1.2 : 0) : 0;
  const title = titleEm * 0.92 * titleLines + 0.3 + (both && board.settings.titleAr ? titleEm * 0.45 * 1.3 * ARABIC_LINE : 0);
  const tag = board.brand.tagline || board.brand.taglineAr ? 0.7 + 1.15 * 1.45 * 2 + (both && board.brand.taglineAr ? 1.15 * 1.3 * ARABIC_LINE : 0) : 0;
  return brand + title + tag;
}

/* ------------------------------------------------------------------ layout A */

const S = '.mb-t-brush-burger-board';
const DECORATIONS = ['Top-left photo', 'Top-left photo, second', 'Top-right photo', 'Top-right photo, second'];
const TITLE_EM = 8.4;
/** Room left for the corner photos above the side columns. */
const SIDE_TOP = 16;
const PORTRAIT_SCALE = 1.2;
const PORTRAIT_TITLE_EM = 5.8;

function hasCornerPhotos(board: MenuBoardContent): boolean {
  return board.decorMediaIds.some(Boolean);
}

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  if (portrait) {
    // Portrait draws everything 1.2× larger, so the board fills the tall screen.
    const top = Math.max(hasCornerPhotos(board) ? 17 : 0, bbHeaderHeight(board, PORTRAIT_TITLE_EM, 2)) + 1.6;
    const capacity = (120 - 4) / PORTRAIT_SCALE - top - 1;
    return [bbColumn(board, capacity, 40), bbColumn(board, capacity, 40)];
  }
  const height = 67.5 - 4;
  const side = height - (hasCornerPhotos(board) ? SIDE_TOP : 0);
  const middle = height - bbHeaderHeight(board, TITLE_EM, 2) - 2;
  return [bbColumn(board, side, 54), bbColumn(board, middle, 54), bbColumn(board, side, 54)];
}

export const brushBurgerBoard: MenuTemplate = {
  id: 'brush-burger-board',
  name: 'Brush board',
  description:
    'A grunge black burger board in three columns: torn brush-stroke section banners, names and prices over dashed rules, a huge distressed title and burger photos in the top corners.',
  defaultTheme: BB_THEME,
  variants: BB_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { id: BB_FAMILY_ID, name: BB_FAMILY_NAME, layout: 'Three columns' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: DECORATIONS },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const { board } = ctx;
    const decor = board.decorMediaIds
      .slice(0, 4)
      .map((id, i) => bbPhoto(ctx, id, 'bb1-photo', `bb1-photo-${i}`))
      .join('');
    const cols = ctx.page.columns;
    const areas = cols.length === 3 ? ['l', 'm', 'r'] : ['l', 'r'];
    const body = cols
      .map((c, i) => `<div class="bb1-col bb1-col-${areas[i]}">${c.map((b) => bbSection(ctx, b, 'bb1')).join('')}</div>`)
      .join('');
    const photos = hasCornerPhotos(board) ? ' bb1-photos' : '';
    return `${decor}<div class="bb1-grid${photos}">${bbHeader(ctx, 'bb1', 7)}${body}</div>`;
  },

  css: `
${bbRootCss(S)}
${S}{padding:2em 3em}
${bbPhotoCss(S, 'bb1-photo')}
${S} .bb1-photo{position:absolute;z-index:0;width:13.5em}
${S} .bb1-photo-0{top:1.2em;left:2.5em;transform:rotate(-6deg)}
${S} .bb1-photo-1{top:2.4em;left:17em;width:12.5em;transform:rotate(8deg)}
${S} .bb1-photo-2{top:2.4em;right:17em;width:12.5em;transform:rotate(-8deg)}
${S} .bb1-photo-3{top:1.2em;right:2.5em;transform:rotate(6deg)}
${S} .bb1-grid{position:relative;z-index:1;flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
  grid-template-rows:auto 1fr;grid-template-areas:"l h r" "l m r";column-gap:4em}
${S} .bb1-header{grid-area:h;margin-bottom:2em}
${S} .bb1-col{min-width:0;min-height:0;display:flex;flex-direction:column}
${S} .bb1-col-l{grid-area:l}
${S} .bb1-col-m{grid-area:m}
${S} .bb1-col-r{grid-area:r}
${S} .bb1-photos .bb1-col-l,${S} .bb1-photos .bb1-col-r{padding-top:${SIDE_TOP}em}
${S}.mb-portrait .bb1-photo{width:11em}
${S}.mb-portrait .bb1-photo-0{top:2em;left:1.5em}
${S}.mb-portrait .bb1-photo-1{top:10.5em;left:4.5em;width:9.5em}
${S}.mb-portrait .bb1-photo-2{top:10.5em;right:4.5em;left:auto;width:9.5em}
${S}.mb-portrait .bb1-photo-3{top:2em;right:1.5em}
${S}.mb-portrait .bb1-grid{font-size:${PORTRAIT_SCALE}em;grid-template-columns:repeat(2,minmax(0,1fr));grid-template-rows:auto 1fr;grid-template-areas:"h h" "l r";column-gap:2.4em}
${S}.mb-portrait .bb1-header{margin:0 0 1.6em}
${S}.mb-portrait .bb1-photos .bb1-header{margin:0 10.5em 1.6em;min-height:17em}
${S}.mb-portrait .bb1-title:not(.mb-2nd){font-size:${PORTRAIT_TITLE_EM}em}
${S}.mb-portrait .bb1-photos .bb1-col-l,${S}.mb-portrait .bb1-photos .bb1-col-r{padding-top:0}
${bbHeaderCss(S, 'bb1', TITLE_EM)}
${bbRowsCss(S, 'bb1')}
`,
};
